import { useRef, useState } from "react";
import { Box } from "@mui/material";
import { IconInvalidUrl, IconPlay } from "@app/components/icons";
import { INVALID_URL } from "@app/constants";

type InspectionPreviewMediaSlideProps = {
  media: any;
  onError: () => void;
};

function isPreviewMediaVideo(media: any): boolean {
  const mimeType = (media?.mimeType || media?.type || "").toLowerCase();
  return mimeType.includes("video");
}

function getPreviewMediaUrl(media: any): string {
  if (media?.mediaUrl) {
    return media.mediaUrl;
  }

  if (media instanceof File || media instanceof Blob) {
    return URL.createObjectURL(media);
  }

  return "";
}

function getVideoThumbnailSrc(media: any): string {
  return media?.thumbnailUrl || media?.poster || "";
}

export function InspectionPreviewMediaSlide({
  media,
  onError,
}: InspectionPreviewMediaSlideProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  if (media?.mediaUrl === INVALID_URL) {
    return (
      <Box
        sx={{
          width: "100%",
          height: 210,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <IconInvalidUrl />
      </Box>
    );
  }

  const mediaUrl = getPreviewMediaUrl(media);

  if (isPreviewMediaVideo(media)) {
    const thumbnailSrc = getVideoThumbnailSrc(media);

    return (
      <Box className="inspection-preview-video" sx={{ width: "100%" }}>
        <Box
          component="video"
          ref={videoRef}
          className="inspection-preview-video-player"
          src={mediaUrl}
          poster={thumbnailSrc || undefined}
          controls
          playsInline
          preload="metadata"
          onEnded={() => setIsPlaying(false)}
          onPause={() => setIsPlaying(false)}
          onPlay={() => setIsPlaying(true)}
          onError={onError}
        />
        {!isPlaying && (
          <Box
            className="inspection-preview-video-play-icon"
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              backgroundColor: "rgba(0,0,0,0.4)",
              borderRadius: "50%",
              width: 36,
              height: 36,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              pointerEvents: "none",
            }}
          >
            <IconPlay />
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box
      className="swiper-image"
      component="img"
      alt="swiper-image"
      src={mediaUrl}
      onError={onError}
    />
  );
}
